import { useEffect, useState, type ReactNode } from 'react';
import { Icon } from './Icon';

export type ToastTone = 'ok' | 'error' | 'info';
export interface ToastMessage {
  message: string;
  tone: ToastTone;
}

type Listener = (t: ToastMessage) => void;
const listeners = new Set<Listener>();

function emit(message: string, tone: ToastTone) {
  // No subscriber yet (e.g. an error during the very first paint): fail quietly.
  listeners.forEach((l) => l({ message, tone }));
}

/**
 * Fire a toast from anywhere — components, event handlers, or even outside the
 * React tree (the React Query error handler uses this). A mounted ToastProvider
 * renders whatever is emitted; if none is mounted the call is simply a no-op.
 */
export const toast = {
  success: (message: string) => emit(message, 'ok'),
  error: (message: string) => emit(message, 'error'),
  info: (message: string) => emit(message, 'info'),
};

interface ActiveToast extends ToastMessage {
  id: number;
}

let seq = 0;
const AUTO_DISMISS_MS = 4500;
const ICON: Record<ToastTone, 'check' | 'warn' | 'bell'> = { ok: 'check', error: 'warn', info: 'bell' };

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ActiveToast[]>([]);

  useEffect(() => {
    const listener: Listener = (t) => {
      const id = ++seq;
      setItems((prev) => [...prev, { ...t, id }]);
      window.setTimeout(() => {
        setItems((prev) => prev.filter((i) => i.id !== id));
      }, AUTO_DISMISS_MS);
    };
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  }, []);

  const dismiss = (id: number) => setItems((prev) => prev.filter((i) => i.id !== id));

  return (
    <>
      {children}
      <div className="toast-viewport" role="region" aria-label="Notifiche di sistema">
        {items.map((t) => (
          <div key={t.id} className={`toast toast--${t.tone}`} role="status" aria-live="polite">
            <Icon name={ICON[t.tone]} size={15} />
            <span className="toast__msg">{t.message}</span>
            <button className="toast__close" onClick={() => dismiss(t.id)} aria-label="Chiudi notifica">
              <Icon name="x" size={13} />
            </button>
          </div>
        ))}
      </div>
    </>
  );
}
